// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const LEVELS = ['none', 'user', 'admin'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 > Date.now()) {
          await loadUser();
        } else {
          logout();
        }
      } catch {
        logout();
      }
    }
    setLoading(false);
  };

  const loadUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Error loading user:', error);
      logout();
    }
  };

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/login', { username, password });
      const { access_token, refresh_token } = response.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      // Load full profile (includes module_permissions) rather than using
      // the login response which doesn't carry module permissions.
      await loadUser();
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      };
    }
  };

  const logout = () => {
    localStorage.clear();
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';

  /**
   * Returns true if the current user can access the given module at or above minLevel.
   * Levels: 'none' < 'user' < 'admin'
   * Admins always pass. Missing permission record defaults to 'user' (full access).
   */
  const canAccess = (module, minLevel = 'user') => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const perms = user.module_permissions || {};
    const level = perms[module] ?? 'user';
    return LEVELS.indexOf(level) >= LEVELS.indexOf(minLevel);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, loadUser, canAccess }}>
      {children}
    </AuthContext.Provider>
  );
};
